/**
 * Component is used to show the FormSelect in two different modes
 *
 * @attr {String} displayMode - will be used to render three different type of datepicker: 'native' and 'custom'
 *
 * TAG Structure
 * <!-- Example of select type -->
 *<e2-search-select-field
 *  ...
 *  display-mode="native">
 *</e2-search-select-field>
 *
 * HTML Structure
 * <!-- Example of 'native' type -->
 *<e2-native-search-select
 *  .options="${ this.optionsToJson(selectOptions) }"
 *  .disabled="${disabled}"
 *  @select="${ this.onSelect }"
 *  .selected="${this.selectedValue || this.getSelectedOptionLabel() || this.placeholder}"
 *></e2-native-search-select>
 */

import { html } from 'lit-element';
import { ifDefined } from 'lit-html/directives/if-defined';

import { templateToString } from '@elab/core/utils/template';
import getSelectors from '@elab/core/utils/dynaForms/selectors';
import { selectors as configSelectors } from '@elab/core/store/config';
import connect from '@elab/core/store/connect';
import { cacheSelector } from '@elab/core/utils/webComponent';

import { FormElement } from '../forms/Form';

const PROPERTIES = {
    isOpen: { type: Boolean, attribute: false },
    placeholder: { type: String, attribute: 'placeholder' },
    cssModifier: { type: String, attribute: 'css-modifier' },
    selectFieldModifier: { type: String, attribute: 'select-field-modifier' },
    options: { type: Array },
    customClasses: { type: String, attribute: 'custom-classes' },
    disabled: { type: Boolean },
    optionLabelPattern: { type: String, attribute: 'option-label-pattern' },
    disableOneElement: { type: Boolean, attribute: 'disable-one-element' },
    displayMode: { type: String, attribute: 'display-mode' },
    name: { type: String },
    label: { type: String },
    labelled: { type: Boolean, attribute: false },
    noEmptyLine: { type: Boolean, attribute: 'no-empty-line' },
    disabledNativePlaceholderOption: {
        type: Boolean,
        attribute: 'disabled-native-placeholder-option',
    },
    required: { type: Boolean, reflect: true },
    gtmLabel: {
        type: String,
        attribute: 'gtm-label',
    },
    useLabelAsPlaceholder: {
        type: Boolean,
        attribute: 'use-label-as-placeholder',
    },
    syncSelectValue: { type: Boolean, attribute: 'sync-select-value' },
    useSlotLabelFromParent: { type: Boolean, attribute: 'use-slot-label-from-parent' },
    showProvinceAsAutoComplete: { type: Boolean },
};

const DISPLAY_MODES = {
    NATIVE: 'native',
    CUSTOM: 'custom',
};

export class SearchSelectField extends FormElement {
    static get properties() {
        return PROPERTIES;
    }

    constructor() {
        super();

        this.isOpen = false;
        this.required = false;
        this.selectFieldModifier = '';
        this.gtmLabel = '';
        this.useLabelAsPlaceholder = false;
        this.useSlotLabelFromParent = false;
        this.isIosMobileDevice = navigator.userAgent.match(/iPhone|iPad/i);
    }

    updated(_changedProperties) {
        super.updated(_changedProperties);
        if (_changedProperties.has('fieldValue')) {
            if (this.fieldValue && this.fieldValue.label) {
                this.labelled = true;
            } else {
                this.labelled = false;
            }

            if (this.fieldValue) {
                this.disabled = this.fieldValue.readOnly;
            }
        }
    }

    setColorOption = color => {
        if (color) {
            return html`
                <span class="select__color-variant" style="background-image: url('${color}')" />
            `;
        }
    };

    getSelectedOptionLabel() {
        const { fieldValue = {} } = this;
        const currOption =
            (fieldValue.values || []).find(
                option => option.name === (fieldValue.value || fieldValue.defaultValue),
            ) || {};

        const preview = currOption.imageUrl;
        const label = currOption.label || fieldValue.value;

        return label
            ? html`
                  ${preview ? this.setColorOption(preview) : ''}
                  ${templateToString(currOption.label || fieldValue.value, this.optionLabelPattern)}
              `
            : '';
    }

    setStatus(status) {
        this.isOpen = status;
    }

    toggleStatus() {
        this.setStatus(!this.isOpen);
    }

    get isProvinceField() {
        return this.showProvinceAsAutoComplete && this.name === 'province';
    }

    render() {
        const { cssModifier = '', disabled, isOpen } = this;
        const definedError = this.isErrorDefined && this.isErrorDefined();
        let mod = [];
        if (this.isIosMobileDevice) {
            mod = [
                cssModifier,
                disabled ? 'select--disabled' : '',
                isOpen ? 'select--opened' : '',
                this.isErrorDefined() ? 'select--error' : '',
            ].join(' ');
        } else {
            mod = [
                cssModifier,
                disabled ? 'search-select--disabled' : '',
                isOpen ? 'select--opened' : '',
                this.isErrorDefined() ? 'search-select--error' : '',
            ].join(' ');
        }
        const placeholderOpt = { name: null, label: this.placeholder };
        const values = (this.fieldValue && this.fieldValue.values) || [];
        const selectOptions = this.placeholder ? [placeholderOpt, ...values] : [...values];
        const displayModesData = { mod, selectOptions, disabled };
        if (this.isIosMobileDevice) {
            return html`
                <div class="select-field ${this.selectFieldModifier}">
                    ${this.displayModes(displayModesData)}
                    <v-slot name="tooltip"></v-slot>
                    <v-slot name="required-mark"></v-slot>
                    ${this.isErrorDefined()
                        ? html`
                              <p>
                                  <span class="select select--error-label-icon"></span>
                                  <span class="select select--error-label">${definedError}</span>
                              </p>
                          `
                        : ''}
                </div>
            `;
        }
        return html`
            <div class="search-select-field ${this.selectFieldModifier}">
                ${this.displayModes(displayModesData)}
                <v-slot name="tooltip"></v-slot>
                <v-slot name="required-mark"></v-slot>
                ${this.isErrorDefined()
                    ? html`
                          <p>
                              <span class="search-select search-select--error-label-icon"></span>
                              <span class="search-select search-select--error-label"
                                  >${definedError}</span
                              >
                          </p>
                      `
                    : ''}
            </div>
        `;
    }

    renderAsCustom = displayModesData => {
        const { mod, selectOptions } = displayModesData;
        return html`
            <v-slot name="title"></v-slot>
            <v-slot name="label">
                <p class="select-field__label">${this.fieldValue && this.fieldValue.label}</p>
            </v-slot>
            <label class="select ${mod}" tabindex="0" @blur="${() => this.setStatus(false)}">
                <div class="select__wrapper" @click="${this.toggleStatus}">
                    <span class="select__selected-option">
                        ${this.getSelectedOptionLabel() || this.placeholder}
                    </span>
                    <span class="select__handler">
                        <i class="icon icon-kruidvat-arrow-down"></i>
                    </span>
                </div>
                <div class="select__options-container">
                    ${this.isOpen
                        ? html`
                              <e2-scroll
                                  class="select__items-container"
                                  .active=${this.isOpen}
                                  wheel-propagation="${true}"
                              >
                                  <ul class="select__options">
                                      ${selectOptions.map(
                                          option => html`
                                              <li
                                                  class="select__option"
                                                  @click="${() => this.handleChange(option)}"
                                              >
                                                  ${this.setColorOption(option.imageUrl)}
                                                  ${templateToString(
                                                      option.label,
                                                      this.optionLabelPattern,
                                                  )}
                                              </li>
                                          `,
                                      )}
                                  </ul>
                              </e2-scroll>
                          `
                        : html``}
                </div>
            </label>
        `;
    };

    optionsToJson = array => {
        return array.map(option => ({
            value: option.name,
            label: option.label,
        }));
    };

    onSelect = evt => {
        this.handleChange({ name: evt.detail });
    };

    getPalaceholder = () => {
        return this.label || this.name;
    };

    renderAsNative = displayModesData => {
        if (this.fieldValue) {
            const { selectOptions } = displayModesData;
            const value = this.fieldValue.value || this.fieldValue.defaultValue;
            if (this.isIosMobileDevice) {
                return html`
                    <e2-native-select
                        class="native-select native-select--form-field"
                        class-modifier="select--form-field"
                        .options="${this.optionsToJson(selectOptions)}"
                        ?disabled="${this.fieldValue.readOnly}"
                        @select="${this.onSelect}"
                        default-value="${value || ''}"
                        ?has-errors="${this.isErrorDefined && this.isErrorDefined()}"
                        ?labelled="${this.labelled}"
                        ?no-empty-line="${this.noEmptyLine}"
                        ?sync-select-value="${!!this.syncSelectValue}"
                        gtm-label="${ifDefined(this.gtmLabel || undefined)}"
                        ?disabled-placeholder-option="${this.disabledNativePlaceholderOption}"
                        placeholder="${this.useLabelAsPlaceholder ? this.getPalaceholder() : ''}"
                        required="${this.required}"
                    >
                    </e2-native-select>
                    <p class="select-field__label">${this.fieldValue && this.fieldValue.label}</p>
                `;
            }
            return html`
                <e2-native-search-select
                    class="native-search-select native-search-select--form-field"
                    class-modifier="search-select--form-field"
                    .options="${this.optionsToJson(selectOptions)}"
                    ?disabled="${this.fieldValue.readOnly}"
                    @select="${this.onSelect}"
                    default-value="${value || ''}"
                    ?has-errors="${this.isErrorDefined && this.isErrorDefined()}"
                    ?labelled="${this.labelled}"
                    ?no-empty-line="${this.noEmptyLine}"
                    ?sync-select-value="${!!this.syncSelectValue}"
                    gtm-label="${ifDefined(this.gtmLabel || undefined)}"
                    ?disabled-placeholder-option="${this.disabledNativePlaceholderOption}"
                    placeholder="${this.useLabelAsPlaceholder ? this.getPalaceholder() : ''}"
                    required="${this.required}"
                    name="${this.name}"
                >
                </e2-native-search-select>
                <p class="search-select-field__label">
                    ${this.fieldValue && this.fieldValue.label}
                </p>
            `;
        }
        return html``;
    };

    displayModes(displayModesData) {
        switch (this.displayMode) {
            case DISPLAY_MODES.NATIVE:
                return this.renderAsNative(displayModesData);
            case DISPLAY_MODES.CUSTOM:
            default:
                return this.renderAsCustom(displayModesData);
        }
    }

    handleChange(option) {
        // Changes won't be performed since new value equals to previous one. Actually, nothing has changed.
        if (this.fieldValue.value !== option.name) {
            this.changeValue(option.name);
        }
        this.setStatus(false);
    }
}

const mapStateToProps = (state, ctx) => {
    const selectors = cacheSelector(ctx, getSelectors);
    return {
        required: selectors.isDataFieldRequired(state, ctx.name),
        showProvinceAsAutoComplete: configSelectors.showProvinceAsAutoComplete(state),
    };
};

export default () => {
    customElements.define('e2-search-select-field', connect(mapStateToProps)(SearchSelectField));
};
